import { Logger } from "../../logger.service";

export type MessageType = 'request' | 'response' | 'event' | 'publish';

export interface Message {
  id?: string;
  type: MessageType;
  action: string;
  payload?: any;
  error?: string;
  protocolVersion?: string;
  meta?: Record<string, any>;
}

export interface RequestHandler {
  (payload: any, origin: string): any | Promise<any>;
}

export interface EventHandler {
  (payload: any, origin: string): void;
}

export interface CrossFrameBridgeOptions {
  targetWindow: Window;
  targetOrigin: string;
  allowedOrigins?: string[];
  protocolVersion?: string;
  timeoutMs?: number;
}

interface PendingRequest {
  resolve: (value: any) => void;
  reject: (reason: any) => void;
  timer: ReturnType<typeof setTimeout>;
}

export class CrossFrameBridge {
  private targetWindow: Window;
  private targetOrigin: string;
  private allowedOrigins: string[];
  private protocolVersion?: string;
  private timeoutMs: number;

  private requestHandlers = new Map<string, RequestHandler>();
  private eventHandlers = new Map<string, EventHandler>();
  private publishSubscribers = new Map<string, Set<EventHandler>>();
  private pendingRequests = new Map<string, PendingRequest>();

  protected logger = new Logger('CrossFrameBridge');

  constructor(options: CrossFrameBridgeOptions) {
    this.targetWindow = options.targetWindow;
    this.targetOrigin = options.targetOrigin;
    this.allowedOrigins = options.allowedOrigins ?? [options.targetOrigin];
    this.protocolVersion = options.protocolVersion;
    this.timeoutMs = options.timeoutMs ?? 5000;

    window.addEventListener("message", this.handleIncomingMessage.bind(this));
  }

  destroy(): void {
    window.removeEventListener("message", this.handleIncomingMessage.bind(this));
    this.pendingRequests.clear();
    this.requestHandlers.clear();
    this.eventHandlers.clear();
    this.publishSubscribers.clear();
  }

  protected handleIncomingMessage(event: MessageEvent): void {
    const { origin, data } = event;

    if (!this.allowedOrigins.includes(origin) && !this.allowedOrigins.includes('*')) {
      this.logger.warn(`Ignoring a message received from non allowed origin: ${origin}'. Message: ${data}`);
      return;
    }
    if (typeof data !== "object" || !data.type || !data.action) return;

    const msg = data as Message;
    const { id, type, action, payload, error } = msg;

    const options = { targetOrigin: origin };

    switch (type) {
      case "response":
        if (id && this.pendingRequests.has(id)) {
          const { resolve, reject, timer } = this.pendingRequests.get(id)!;
          clearTimeout(timer);
          this.pendingRequests.delete(id);
          error ? reject(new Error(error)) : resolve(payload);
        }
        break;

      case "request":
        if (!id) return;
        const handler = this.requestHandlers.get(action);
        if (!handler) {
          event.source?.postMessage({ type: "response", id, error: `Unknown action: ${action}` }, options);
          return;
        }

        Promise.resolve(handler(payload, origin))
          .then(result => {
            event.source?.postMessage({ type: "response", id, action, payload: result }, options);
          })
          .catch(err => {
            event.source?.postMessage({ type: "response", id, action, error: err.message }, options);
          });
        break;

      case "event":
        const evtHandler = this.eventHandlers.get(action);
        if (evtHandler) evtHandler(payload, origin);
        break;

      case "publish":
        const subscribers = this.publishSubscribers.get(action);
        if (subscribers) {
          for (const subscriber of subscribers) {
            try {
              subscriber(payload, origin);
            } catch (err) {
              this.logger.error(`Publish handler for '${action}' failed:`, err);
            }
          }
        }
        break;
    }
  }

  public sendRequest<T = any>(action: string, payload: any): Promise<T> {
    const id = crypto.randomUUID();
    const message: Message = {
      id,
      type: "request",
      action,
      payload,
      protocolVersion: this.protocolVersion,
    };

    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pendingRequests.delete(id);
        reject(new Error(`Timeout for action: ${action}`));
      }, this.timeoutMs);

      this.pendingRequests.set(id, { resolve, reject, timer });
      this.targetWindow.postMessage(message, this.targetOrigin);
    });
  }

  public onRequest(action: string, handler: RequestHandler): void {
    this.requestHandlers.set(action, handler);
  }

  public sendEvent(action: string, payload: any): void {
    const message: Message = {
      type: "event",
      action,
      payload,
      protocolVersion: this.protocolVersion,
    };
    this.targetWindow.postMessage(message, this.targetOrigin);
  }

  public onEvent(action: string, handler: EventHandler): void {
    this.eventHandlers.set(action, handler);
  }

  public publish(action: string, payload: any): void {
    const message: Message = {
      type: "publish",
      action,
      payload,
      protocolVersion: this.protocolVersion,
    };
    this.targetWindow.postMessage(message, this.targetOrigin);
  }

  public onPublish(action: string, handler: EventHandler): () => void {
    if (!this.publishSubscribers.has(action)) {
      this.publishSubscribers.set(action, new Set());
    }
    const handlers = this.publishSubscribers.get(action)!;
    handlers.add(handler);

    return () => {
      handlers.delete(handler);
      if (handlers.size === 0) {
        this.publishSubscribers.delete(action);
      }
    };
  }
}
